import React, { useState, useRef, useCallback } from 'react';

/**
 * Before/After image comparison slider.
 * Drag the handle to reveal original (left) vs AI-processed (right).
 */
export default function BeforeAfterSlider({ beforeSrc, afterSrc }) {
  const [position, setPosition] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const handleMove = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.max(2, Math.min(98, x)));
  }, []);

  const onPointerDown = useCallback((e) => {
    dragging.current = true;
    handleMove(e.clientX);
    e.preventDefault();
  }, [handleMove]);

  const onPointerMove = useCallback((e) => {
    if (!dragging.current) return;
    handleMove(e.clientX);
  }, [handleMove]);

  const onPointerUp = useCallback(() => {
    dragging.current = false;
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full select-none cursor-col-resize rounded-xl overflow-hidden border border-white/10 shadow-2xl"
      style={{ aspectRatio: 'auto' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      data-testid="before-after-slider"
    >
      {/* After image (full, underneath) */}
      <img src={afterSrc} alt="After" className="w-full block" draggable={false} />

      {/* Before image (clipped from left) */}
      <div
        className="absolute top-0 left-0 h-full overflow-hidden"
        style={{ width: `${position}%` }}
      >
        <img
          src={beforeSrc}
          alt="Before"
          className="block h-full"
          style={{ width: `${containerRef.current ? containerRef.current.offsetWidth : 800}px`, maxWidth: 'none' }}
          draggable={false}
        />
      </div>

      {/* Divider line */}
      <div
        className="absolute top-0 h-full"
        style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
      >
        <div className="w-0.5 h-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.6)]" />
        {/* Handle */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-[0_0_15px_rgba(0,240,255,0.5)] flex items-center justify-center">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M5 3L2 8L5 13" stroke="#0A101D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M11 3L14 8L11 13" stroke="#0A101D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>

      {/* Labels */}
      <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm text-white text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-full">
        Before
      </div>
      <div className="absolute top-3 right-3 bg-cyan-500/80 backdrop-blur-sm text-black text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-full">
        After
      </div>
    </div>
  );
}
